import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getMethod, postMethod } from "../../services/ApiCallMethods";

export const getClientProperties = createAsyncThunk(
  "clientAdvisorManagement/getProperties",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await getMethod(
        `/advisor/getClientProperty?client_id=${clientId}`
      );
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch properties"
      );
    }
  }
);

export const sendInviteToAdvisor = createAsyncThunk(
  "clientAdvisorManagement/sendInvite",
  async (data, { rejectWithValue }) => {
    try {
      const response = await postMethod(`/advisor/sendInvite`, data);
      const json = await response.json();
      if (json.status === "ERROR") {
        return rejectWithValue(json.message || "Failed to send invite");
      }

      return json;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Failed to send invite");
    }
  }
);

export const getAdvisorsByClientId = createAsyncThunk(
  "clientAdvisorManagement/getAdvisors",
  async ({ currentPage, itemsPerPage, clientId, search = "", investmentAmount, investmentType }, { rejectWithValue }) => {
    try {
      let queryParams = `client_id=${clientId}&page=${currentPage}&limit=${itemsPerPage}&search=${search}`;

      if (investmentAmount) {
        queryParams += `&investment_amount=${investmentAmount}`;
      }
      if (investmentType) {
        queryParams += `&investments=${investmentType}`;
      }

      const response = await getMethod(
        `/advisor/getAdvisorsByClientId?${queryParams}`
      );
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch advisors"
      );
    }
  }
);

export const updateAdvisorProfile = createAsyncThunk(
  "clientAdvisorManagement/updateAdvisorProfile",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await postMethod(
        `/advisor/updateAdvisorProfile/${id}`,
        data
      );
      const json = await response.json();
      return json;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || "Failed to update advisor"
      );
    }
  }
);

// Initial state
const initialState = {
  properties: [],
  loading: false,
  error: null,
  inviteSuccess: null,
  inviteError: null,
  inviteLoading: false,
  advisors: [],
  advisorsLoading: false,
  advisorsError: null,
  pagination: {},
};

// Slice
const advisorClientPropertySlice = createSlice({
  name: "clientAdvisorManagement",
  initialState,
  reducers: {
    clearProperties(state) {
      state.properties = [];
      state.error = null;
    },
    clearInviteStatus(state) {
      state.inviteSuccess = null;
      state.inviteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClientProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.properties = []; // Clear old data
      })
      .addCase(getClientProperties.fulfilled, (state, action) => {
        state.loading = false;
        state.properties = action.payload;
      })
      .addCase(getClientProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      .addCase(sendInviteToAdvisor.pending, (state) => {
        state.inviteLoading = true;
        state.inviteError = null;
      })
      .addCase(sendInviteToAdvisor.fulfilled, (state, action) => {
        state.inviteLoading = false;
        state.inviteSuccess = action.payload;
      })
      .addCase(sendInviteToAdvisor.rejected, (state, action) => {
        state.inviteLoading = false;
        state.inviteError = action.payload || "Failed to send invite";
      })
      .addCase(getAdvisorsByClientId.pending, (state) => {
        state.advisorsLoading = true;
        state.advisorsError = null;
        state.advisors = []; // Clear old data
        state.pagination = {}; // Clear old pagination
      })
      .addCase(getAdvisorsByClientId.fulfilled, (state, action) => {
        state.advisorsLoading = false;
        state.advisors = action.payload.advisors || [];
        state.pagination = action.payload.pagination
      })
      .addCase(getAdvisorsByClientId.rejected, (state, action) => {
        state.advisorsLoading = false;
        state.advisorsError = action.payload || "Something went wrong";
      })
      .addCase(updateAdvisorProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAdvisorProfile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAdvisorProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update advisor";
      });
  },
});

export const { clearProperties } = advisorClientPropertySlice.actions;
export default advisorClientPropertySlice.reducer;
